import React, { useEffect, useRef, useMemo } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import dayjs from "dayjs";
import _ from 'lodash';
import { ToggleButton, ToggleButtonGroup } from "./CustomToggleButton";

const SCurveChart = ({
  height, width, maxYaxis,
  data,
  chart_id,
  columnItems,
  lineItems,
  unit,
  lineLabel,
  category, maxWidth,
  actual_mark_use,
  side_unit_lable, legendContainer
}) => {

  const chartRef = useRef(null);

  const [checked, setChecked] = React.useState("월간");
  const weekly = checked === "주간";


  const onCheckedChange = (event, selectedValue) => {
    if (selectedValue !== null) {
      setChecked(selectedValue);
    }
  };

  const chartData = useMemo(() => {
    if (!data) return [];
    return checked === "주간" ? data.weekProgress : data.monthProgress;
  }, [data, checked]);




  useEffect(() => {
    if (!chartRef.current || !legendContainer) return;

    let chartRoot = am5.Root.new(chart_id);
    chartRoot.setThemes([am5themes_Animated.new(chartRoot)]);

    let chart = chartRoot.container.children.push(
      am5xy.XYChart.new(chartRoot, {
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: chartRoot.verticalLayout,
        // maxTooltipDistance: -1,
      })
    );


    chart.plotContainer.onPrivate("height", function(height) {
      chart.zoomOutButton.set("dy", height - 45);
      chart.zoomOutButton.set('scale',0.8)

    });
    // chart.zoomOutButton.set("forceHidden", true);

    let tooltip = am5.Tooltip.new(chartRoot, {
    });

    chart.plotContainer.set("tooltipPosition", "pointer");
    chart.plotContainer.set("tooltipText", "a");
    chart.plotContainer.set("tooltip", tooltip);

    tooltip.label.adapters.add("text", function (text, target) {
      text = "";
      let i = 0;
      chart.series.each(function (series) {
        let tooltipDataItem = series.get("tooltipDataItem");
    
        if (tooltipDataItem) {
          if (i === 0) {
            text += "[bold fontSize:14px]" + tooltipDataItem.get("categoryX") + "\n";
          }
    
          if (i !== 0) {
            text += "\n";
          }
    
          if (tooltipDataItem.get("valueY")) {
            // Get the color of the series' stroke and convert it to a hex string
            let strokeColor = series.get("stroke"); // Remove the '#' character
    
            // Determine the symbol based on the series type
            let symbol = "■"; // Default symbol for ColumnSeries
            if (series instanceof am5xy.LineSeries) {
              symbol = "—";
            }
    
            text +=
              "["+
              strokeColor +
              "]" +
              symbol +
              "[/][width:8px] [fontSize: 14px]" +
              series.get("name") +
              ": " +
              Number(
                (tooltipDataItem.get("valueY") ?? 0).toFixed(2)
              ).toLocaleString() +
              " " +
              (unit ? unit : series.get("name").includes("시간") ? "h" : "%");
          }
        }
        i++;
      });
      return text;
    });

    tooltip.get("background").setAll({
      stroke: am5.color(0x000000),
      strokeOpacity: 0.8,
      fill: am5.color(0xffffff),
      fillOpacity: 0.8,
    });


    // Add cursor
    // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
    let cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(chartRoot, {
        behavior: "panXY",
      })
    );

    cursor.lineX.setAll({
      stroke: "#525252",
      strokeWidth: 1,
      strokeDasharray: [5, 5],
    });

    cursor.lineY.setAll({
      stroke: "#525252",
      strokeWidth: 1,
      strokeDasharray: [5, 5],
    });

    // cursor.lineY.set("visible", false);

    // let data = [...data];
    var legendRoot = am5.Root.new(legendContainer);

    legendRoot.setThemes([
      am5themes_Animated.new(chartRoot)
    ]);

    var legend = legendRoot.container.children.push(
      am5.Legend.new(legendRoot, {
        // width: am5.percent(0),
        // centerX: 350,
        // x:350,
        centerY: am5.percent(0),
        y: am5.percent(0),
        layout: am5.GridLayout.new(legendRoot, {
          maxColumns: 4,
          fixedWidthGrid: true
        })
      })
    );

    legend.valueLabels.template.set("forceHidden", true);


    legend.markers.template.setAll({
      width: 10,
      height: 10,
    });

    legend.labels.template.setAll({
      fontSize: 8,
      fontWeight: "400",
      fill: "#525252",
    });
    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    let xRenderer = am5xy.AxisRendererX.new(chartRoot, {
      minGridDistance: 30,
      cellStartLocation: 0.1,
      cellEndLocation: 0.9,
      strokeOpacity: 0,
      // strokeWidth: 1,
      // stroke: "rgba(224, 224, 224, 0.5)",
    });

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(chartRoot, {
        categoryField: category,
        renderer: xRenderer,
      })
    );

    xRenderer.grid.template.setAll({
      location: 1,
      strokeOpacity: 0,
    });

    xRenderer.labels.template.adapters.add("text", (text, target) => {
      if (target.dataItem) {

        const category = target.dataItem.dataContext.date;

        if (category.includes("월")) {
          const date = dayjs(category, "YY년 MM월");

          const valueList = target.dataItem.component._data._values;
          const categoryIndex = _.findIndex(valueList, { date: category })

          const year = date.year();
          const month = date.month() + 1;

          if (categoryIndex === 0 || year !== dayjs(target.dataItem.component._data._values[categoryIndex - 1]?.date, "YY년 MM월").year()) {
            return category.split(" ")[0];
          } else {
            return `${month}월`;
          }
        } else {
          const date = dayjs(category, "YY년 WW주차");

          const valueList = target.dataItem.component._data._values;
          const categoryIndex = _.findIndex(valueList, { date: category })

          const year = date.year();

          if (categoryIndex === 0 || year !== dayjs(target.dataItem.component._data._values[categoryIndex - 1]?.date, "YY년 WW주차").year()) {
            return category.split(" ")[0];
          } else {
            return category.split(" ")[1].slice(0, 3);
          }
        }
        return text;
      }
    });


    // xRenderer.labels.template.setAll({
    //   paddingTop: 10,
    //   scale: 0.7,
    //   fill: "#525252",
    // });

    xAxis.data.setAll(chartData);

    xAxis.get("renderer").labels.template.setAll({
      // oversizedBehavior: "fit",
      oversizedBehavior: "wrap",
      textAlign: "center",
      fill: "#525252",
      maxWidth: 50,
      fontSize: "0.8rem",
      minScale: 0.6
    });

    let yRenderer = am5xy.AxisRendererY.new(chartRoot, {
      strokeOpacity: 1,
      strokeWidth: 1,
      stroke: "rgba(224, 224, 224, 0.5)",
      grid: {
        disabled: true,
      },
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(chartRoot, {
        strictMinMaxSelection: true,
        extraMin: 0,
        // extraMax: 0.06,
        renderer: yRenderer,
      })
    );

    if (unit !== "" && side_unit_lable) {
      let yAxislabel = am5.Label.new(chartRoot, {
        rotation: 0,
        text: unit,
        y: am5.p50,
        centerX: am5.p50,
        fill: "#FFF",
        //x: am5.p0,
        //centerY: am5.p0
      });

      yAxis.children.unshift(yAxislabel);
    }

    yRenderer.grid.template.setAll({
      strokeOpacity: 0,
    });

    yRenderer.labels.template.setAll({
      paddingRight: 10,
      scale: 0.7,
      fill: "#525252",
    });

    yAxis.set("numberFormat", "#,###");


    let paretoAxisRenderer = am5xy.AxisRendererY.new(chartRoot, {
      opposite: true,
      strokeOpacity: 1,
      strokeWidth: 1,
      stroke: "rgba(224, 224, 224, 0.5)",
    });

    let paretoAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(chartRoot, {
        strictMinMaxSelection: true,
        // extraMin: 0,
        // extraMax: 0.03,
        max: maxYaxis,
        renderer: paretoAxisRenderer,
        visible: lineLabel,
      })
    );

    paretoAxisRenderer.grid.template.setAll({
      strokeOpacity: 0,
    });

    paretoAxisRenderer.labels.template.setAll({
      paddingLeft: 10,
      scale: 0.7,
      fill: "#525252",
      text: "asd",
    });

    chart.yAxes.autoDispose = false;
    // paretoAxisRenderer.grid.template.set("forceHidden", true);
    // paretoAxis.set("numberFormat", "#");

    // Add series
    // https://www.amcharts.com/docs/v5/charts/xy-chart/series/

    const makeSeries = (name, fieldName, color) => {
      let series = chart.series.push(
        am5xy.ColumnSeries.new(chartRoot, {
          name: name,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: fieldName,
          categoryXField: category,
          fill: color,
          stroke: color,
          // legendLabelText: name + " {valueY.formatNumber('#,###.#')}",
          // legendRangeLabelText: name,
        })
      );

      series.data.setAll(chartData);
      series.columns.template.setAll({
        width: am5.percent(100)
      });

      // series.columns.template.set("fillGradient", am5.LinearGradient.new(chartRoot, {
      //   stops: [{
      //     color: am5.color(color),
      //     opacity: 0.8
      //   }, {
      //     color: am5.color(color),
      //     opacity: 0
      //   }],
      //   rotation: 90
      // }));
      // Make stuff animate on load
      // https://www.amcharts.com/docs/v5/concepts/animations/
      series.appear();

      series.bullets.push(function () {
        return am5.Bullet.new(chartRoot, {
          locationY: 0,
          sprite: am5.Label.new(chartRoot, {
            // text: "{valueY}",
            fill: chartRoot.interfaceColors.get("alternativeText"),
            centerY: 0,
            centerX: am5.p50,
            populateText: true,
          }),
        });
      });

      legend.data.push(series);
    };

    legend.labels.template.setAll({
      fontSize: "0.8rem",
      fontWeight: "500"
    });


    const makeParetoSeries = (name, fieldName, color) => {
      let paretoSeries = chart.series.push(
        am5xy.LineSeries.new(chartRoot, {
          name: name,
          xAxis: xAxis,
          yAxis: paretoAxis,
          valueYField: fieldName,
          categoryXField: category,
          stroke: color,
          fill: color,
          // maskBullets: false,
          // tooltip: am5.Tooltip.new(chartRoot, {
          //   labelText: "[bold]{name}[/]\n{valueX.formatDate()}: {valueY}",
          // }),
          // legendLabelText: name + " {valueY.formatNumber('#,###.#')}",
          legendRangeLabelText: name,
        })
      );

      paretoSeries.strokes.template.setAll({
        strokeWidth: 2,
      });

      paretoSeries.bullets.push(function () {
        return am5.Bullet.new(chartRoot, {
          locationY: 1,
          sprite: am5.Circle.new(chartRoot, {
            radius: weekly && height < 410 ? 0 : 3,
            stroke: paretoSeries.get("stroke"),
            strokeWidth: 2,
            fill: am5.color(0xffffff),
          }),
        });
      });

      paretoSeries.data.setAll(chartData);

      legend.data.push(paretoSeries);
    };

    const realColumnItems = checked === '주간' ? columnItems.week : columnItems.month;

    realColumnItems.forEach((com) => {
      const { name, field_name, color } = com;

      makeSeries(name, field_name, color);
    });

    lineItems.forEach((com) => {
      const { name, field_name, color } = com;

      makeParetoSeries(name, field_name, color);
    });

    // Create axis ranges
    function createRange(value, endValue, color) {
      let rangeDataItem = yAxis.makeDataItem({
        value: value,
        endValue: endValue,
      });

      let range = yAxis.createAxisRange(rangeDataItem);

      if (endValue) {
        range.get("axisFill").setAll({
          fill: color,
          fillOpacity: 0.2,
          visible: true,
        });

        range.get("label").setAll({
          fill: am5.color(0xffffff),
          // text: value + "-" + endValue,
          location: 1,
          // background: am5.RoundedRectangle.new(chartRoot, {
          //   fill: color,
          // }),
        });
      } else {
        range.get("label").setAll({
          fill: am5.color(0xffffff),
          // text: value,
          // background: am5.RoundedRectangle.new(chartRoot, {
          //   fill: color,
          // }),
        });
      }

      range.get("grid").setAll({
        stroke: color,
        strokeOpacity: 1,
        location: 1,
      });
    }

    createRange(0, undefined, "rgba(224, 224, 224, 0.5)");

    const create_xAxis_list = (startValue, endValue) => {
      let rangeDataItem = xAxis.makeDataItem({
        category: startValue,
        endCategory: endValue,
      });

      xAxis.createAxisRange(rangeDataItem);

      rangeDataItem.get("grid").setAll({
        strokeOpacity: 1,
        stroke: "#A3CFFF",
        strokeWidth: 2,
        fill: "#525252",
      });

      rangeDataItem.get("axisFill").setAll({
        fill: "rgba(163, 207, 255, 0.1)",
        visible: true,
      });

      rangeDataItem.get("label").setAll({
        visible: false,
      });
    };

    if (actual_mark_use && data && data.length > 0) {
      create_xAxis_list(data[0].category, dayjs().format("YY년 MM월"));
    }

    chart.appear(1000, 100);

    return () => {

      chartRoot && chartRoot.dispose();
      legendRoot && legendRoot.dispose();
    };
  }, [
    chartData,
    columnItems,
    lineItems,
    unit,
    lineLabel,
    category,
    actual_mark_use,
    side_unit_lable,
    width, chartRef, legendContainer
  ]);

  return (
    <div style={{ position: 'relative', width: '100%', height: height }}>
      {/* ToggleButtonGroup in the top right corner */}
      <div style={{ position: 'absolute', top: 0, left: 0 }}>
        <ToggleButtonGroup
          className="no-drag-button"
          size="small"
          onChange={onCheckedChange}
          value={checked}
          exclusive
          aria-label="baseunit"
        >
          <ToggleButton value="월간" aria-label="monthly">
            월간
          </ToggleButton>
          <ToggleButton value="주간" aria-label="weekly">
            주간
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* Chart div centered and 100% width, positioned at the bottom */}
      <div
        id={chart_id}
        ref={chartRef}
        style={{ position: 'absolute', bottom: 0, width: '100%', height: 'calc(100% - 40px)' }}
      />
    </div>
  );
};

export default SCurveChart;

